import "webrtc-adapter";
import { call, delay } from "@redux-saga/core/effects";
import * as Bowser from "bowser";
export const STREAM_ID_HEADER = "X-Stream-Id";
export const STREAM_TYPE_HEADER = "X-Stream-Type";
export const CONFERENCE_MODE_HEADER = "X-Conference-Mode";
export const CONFERENCE_ID_HEADER = "X-Conference-Id";
export const P2P_STREAM_ID_HEADER = "X-P2p-Stream-Id";
export const ICE_EXCHANGE_HEADER = "X-Ice-Exchange";
export const DOMAIN = "auvious.com";
export const CONFERENCE_DOMAIN = "conference.auvious.com";
export const STREAM_DOMAIN = "stream.auvious.com";
export const CONFERENCE_FACTORY = "conference.factory";
export const SESSION_ID_HEADER = "X-Session-Id";
export const AUVIOUS_CONFERENCE_CLIENT_VERSION = "1.0";
export const AUVIOUS_CONFERENCE_CLIENT_HEADER = "X-Auvious-Conference-Client";
export const KEEPALIVE_SECONDS = 10;
export const TRANSCEIVER_STOPPED_FIELD = "stopped";
export { v4 as uuid } from "uuid";
export function sipURI(username) {
    return "sip:" + username + "@" + DOMAIN;
}
export function conferenceSipURI(conferenceId) {
    return "sip:" + conferenceId + "@" + CONFERENCE_DOMAIN;
}
export function conferenceFactorySipURI() {
    return sipURI(CONFERENCE_FACTORY);
}
export function uriToEndpoint(uri) {
    let transport = "udp";
    if (uri.parameters.transport) {
        transport = uri.parameters.transport;
    }
    return `${uri.scheme}:${uri.user}@${uri.host}:${uri.port};${transport}`;
}
export function getCrossBrowserRtcOfferConstraints(rtcOfferOptions) {
    return rtcOfferOptions;
}
export function getCrossBrowserRtcAnswerConstraints(rtcAnswerOptions) {
    return getCrossBrowserRtcOfferConstraints(rtcAnswerOptions);
}
// export const uuid = _uuid;
export const ICE_SERVERS = [
    {
        urls: ["stun:148.251.144.85:443"],
    },
    {
        urls: ["turn:148.251.144.85:443?transport=tcp"],
        username: "username",
        credential: "password",
    },
];
export function closeMediaStream(stream) {
    if (!stream) {
        return;
    }
    // Latest spec states that MediaStream has no stop() method and instead must
    // call stop() on every MediaStreamTrack.
    try {
        if (stream.getTracks) {
            const tracks = stream.getTracks();
            for (const track of tracks) {
                track.stop();
            }
        }
        else {
            let tracks = stream.getAudioTracks();
            for (const track of tracks) {
                track.stop();
            }
            tracks = stream.getVideoTracks();
            for (const track of tracks) {
                track.stop();
            }
        }
    }
    catch (error) {
        // workaround typescript stop not found
        // @ts-ignore
        stream.stop = stream.stop || console.log;
        // Deprecated by the spec, but still in use.
        // NOTE: In Temasys IE plugin stream.stop is a callable 'object'.
        // @ts-ignore
        stream.stop();
    }
}
/**
 * A saga helper function that mimics retry effect but also
 * takes context of function and premise function.
 * For convenience use with yield*
 *
 * @param maxtries
 * @param wait - delay between retrials
 * @param premise - function that takes as input exceptions of `fn` and returns
 * boolean to continue trials
 * @param cfn - an array [context, function in context]
 * @param args - args to pass to function
 */
export function* retryIf(maxtries, wait, premise, cfn, ...args) {
    let latestError = null;
    while (maxtries-- > 0) {
        try {
            // @ts-ignore
            return yield call(cfn, ...args);
        }
        catch (e) {
            latestError = e;
            if (maxtries !== 0 && premise(e)) {
                yield delay(wait);
            }
            else {
                break;
            }
        }
    }
    throw latestError;
}
/**
 * To be used as premise in retryRequests
 *
 * @param error - the errored response
 * @return boolean - whether passes premise
 */
export function shouldBeRetried(error) {
    return (!error.response ||
        (error.response.status >= 500 && error.response.status < 600) ||
        error.response.status === 429);
}
/**
 * Controll when a promise resolves externally
 */
export class Promisable {
    constructor() {
        this.promises = new Map();
    }
    promise(key) {
        if (this.promises.has(key)) {
            const promise = this.promises.get(key);
            this.promises.delete(key);
            return promise;
        }
        else {
            return new Promise((resolve, reject) => {
                this.promises.set(key, { resolve, reject });
            });
        }
    }
    resolve(key, value) {
        if (this.promises.has(key)) {
            const promise = this.promises.get(key);
            this.promises.delete(key);
            if ("resolve" in promise) {
                promise.resolve(value);
            }
        }
        else {
            this.promises.set(key, Promise.resolve(value));
        }
    }
    reject(key, value) {
        if (this.promises.has(key)) {
            const promise = this.promises.get(key);
            this.promises.delete(key);
            if ("reject" in promise) {
                promise.reject(value);
            }
        }
        else {
            this.promises.set(key, Promise.reject(value));
        }
    }
    clear() {
        for (const [, promise] of this.promises) {
            if (typeof promise.then !== "function") {
                promise.reject();
            }
        }
        this.promises.clear();
    }
}
export function detectBrowser() {
    if (!window) {
        return null;
    }
    const browser = Bowser.getParser(window.navigator.userAgent);
    return {
        osName: browser.getOSName(),
        osVersion: browser.getOSVersion(),
        browserName: browser.getBrowserName(),
        browserVersion: browser.getBrowserVersion(),
    };
}
export function isLocalStorageSupported() {
    return navigator.cookieEnabled;
}
export const CREATED_BY_US = "created-by-auvious-rtc-sdk";
